import {
  LosFieldValueSource,
  UpdateLosFieldConfig,
} from 'src/services/loans/types';

/**
 * Validates an "Update LOS Field" rule-action config. Returns the first
 * user-facing error message, or `null` when the config is valid (or disabled).
 *
 * Shared by both rule builders (loan-level and admin checklist management) so
 * the rules stay in lock-step:
 *   enabled ⇒ a target must be chosen
 *           ⇒ at least one of On Pass / On Fail must be configured
 *           ⇒ each configured value source must be complete.
 */
export function getUpdateLosFieldError(
  config: UpdateLosFieldConfig | undefined,
): string | null {
  // Nothing to validate unless the action is enabled.
  if (!config?.enabled) return null;

  // 1. A target must be chosen.
  if (config.target.kind === 'field') {
    if (!config.target.encompassFieldId?.trim()) {
      return 'Update LOS Field: choose a target Encompass field.';
    }
  } else if (!config.target.milestone?.trim()) {
    return 'Update LOS Field: enter a target milestone.';
  }

  // 2. At least one of On Pass / On Fail must be configured.
  if (!config.onTrue && !config.onFalse) {
    return 'Update LOS Field: configure a value for On Pass or On Fail.';
  }

  // 3. Each configured value source must be complete.
  return (
    validateSource(config.onTrue, 'On Pass') ??
    validateSource(config.onFalse, 'On Fail')
  );
}

function validateSource(
  source: LosFieldValueSource | undefined,
  label: string,
): string | null {
  if (!source) return null;
  switch (source.type) {
    case 'static':
      return source.value?.trim()
        ? null
        : `Update LOS Field: enter a ${label} static value.`;
    case 'expression':
      return source.expression?.trim()
        ? null
        : `Update LOS Field: enter a ${label} expression.`;
    case 'docField':
      return source.documentType?.trim() && source.field?.trim()
        ? null
        : `Update LOS Field: choose a ${label} document and field.`;
    case 'ruleResult':
      // No additional input required.
      return null;
    default:
      return null;
  }
}
